import { MD3LightTheme, useTheme } from 'react-native-paper';

import { colorPrimary } from './colorPrimary';
import { colorSemantic } from './colorSemantic';

export const unisonColors = {
  brandPurple: colorPrimary.purple400,
  brandPurpleLight: colorPrimary.purple200,
  brandPurpleDark: colorPrimary.purple500,

  brandGreenStrong: colorPrimary.lime300,
  brandGreenStrongDark: '#66a80f',
  brandGreenWeak: colorPrimary.lime100,
  iconBrandBase: colorPrimary.lime200,
  colorIconBrandStrong: colorPrimary.lime400,

  bgNdStrong: colorSemantic.colorBgNdStrong,
  textNdPrimary: colorSemantic.colorTextNdPrimary,
  textNdSecondary: colorSemantic.colorTextNdSecondary,
  textNdMuted: colorSemantic.colorTextNdTernary,
  iconNdPrimary: colorSemantic.colorIconNdPrimary,
  borderNdBase: colorSemantic.colorBorderNdBase,
  borderNdInactive: '#dee2e6',
  borderNdStronger: colorSemantic.colorBorderNdStronger,

  infoContainer: colorSemantic.colorBgInfoWeaker,
  onInfoContainer: colorSemantic.colorTextInfoStrong,

  warningContainer: colorSemantic.colorBgWarningWeak,
  onWarningContainer: colorSemantic.colorTextWarningStrong,

  error: colorSemantic.colorBgErrorStrong,
  errorContainer: colorSemantic.colorBgErrorWeak,
  onErrorContainer: colorSemantic.colorTextErrorStrong,

  success: colorSemantic.colorBgSuccessBase,

  recordingRed: '#F44336',

  bgNlBase: colorSemantic.colorBgNlBase,
  bgNlWeak: colorSemantic.colorBgNlWeak,
  bgNlStronger: colorSemantic.colorBgNlStronger,

  bgInfoWeakest: colorSemantic.colorBgInfoWeakest,

  textNdDisabled: colorSemantic.colorTextNdDisabled,

  background: colorPrimary.nl0,
  surfaceContainer: colorPrimary.nl0,
  surfaceVariant: colorPrimary.nl0,
  outlineVariant: colorSemantic.colorBorderNdBase,
  onSurface: colorSemantic.colorTextNdPrimary,
  onSurfaceVariant: '#49454F',

  white: colorPrimary.nl0,
  black: colorPrimary.black,
} as const;

const extendedColors = {
  brandPurpleLight: unisonColors.brandPurpleLight,
  brandPurpleDark: unisonColors.brandPurpleDark,
  textNdPrimary: unisonColors.textNdPrimary,
  textNdSecondary: unisonColors.textNdSecondary,
  textNdMuted: unisonColors.textNdMuted,
  iconNdPrimary: unisonColors.iconNdPrimary,
  warningContainer: unisonColors.warningContainer,
  onWarningContainer: unisonColors.onWarningContainer,
  success: unisonColors.success,
  recordingRed: unisonColors.recordingRed,
  bgNlBase: unisonColors.bgNlBase,
  bgNlWeak: unisonColors.bgNlWeak,
  bgNlStronger: unisonColors.bgNlStronger,
  bgInfoWeakest: unisonColors.bgInfoWeakest,
  textNdDisabled: unisonColors.textNdDisabled,
};

export const appTheme = {
  ...MD3LightTheme,
  colors: {
    ...MD3LightTheme.colors,
    primary: unisonColors.brandPurple,
    onPrimary: unisonColors.white,
    primaryContainer: '#EDE7F1',
    onPrimaryContainer: unisonColors.textNdPrimary,
    secondary: unisonColors.brandGreenStrong,
    onSecondary: unisonColors.white,
    secondaryContainer: unisonColors.brandGreenWeak,
    onSecondaryContainer: unisonColors.textNdPrimary,
    tertiary: unisonColors.onInfoContainer,
    onTertiary: unisonColors.white,
    tertiaryContainer: unisonColors.infoContainer,
    onTertiaryContainer: unisonColors.onInfoContainer,
    error: unisonColors.error,
    onError: unisonColors.white,
    errorContainer: unisonColors.errorContainer,
    onErrorContainer: unisonColors.onErrorContainer,
    background: unisonColors.background,
    onBackground: unisonColors.onSurface,
    surface: unisonColors.background,
    onSurface: unisonColors.onSurface,
    surfaceVariant: unisonColors.surfaceVariant,
    onSurfaceVariant: unisonColors.textNdSecondary,
    surfaceContainer: unisonColors.surfaceContainer,
    outline: unisonColors.borderNdBase,
    outlineVariant: unisonColors.outlineVariant,
    backdrop: 'rgba(23, 52, 74, 0.8)',
    elevation: {
      ...MD3LightTheme.colors.elevation,
      level1: unisonColors.white,
      level2: unisonColors.white,
      level3: unisonColors.white,
      level4: unisonColors.white,
      level5: unisonColors.white,
    },
    ...extendedColors,
  },
};

export type AppTheme = typeof appTheme;

export const useAppTheme = () => useTheme<AppTheme>();

export { colorPrimary, type ColorPrimaryKey } from './colorPrimary';
export { colorSemantic, type ColorSemanticKey } from './colorSemantic';
export { spacing, type SpacingStep } from './spacing';
export { radii, type RadiusKey } from './radii';
export { iconSize, type IconSizeKey } from './iconSize';
export { fontSize, lineHeight, type FontSizeKey } from './fontSize';
export {
  rippleOnDark,
  rippleOnLink,
  rippleOnOutline,
  rippleOnPrimary,
  rippleOnSurface,
} from './ripple';
